import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';
import type { UserResponse, ConversationsResponse } from '@/lib/types';
import { ChatView } from '@/components';

function ExpenseDisputeChatPage() {
  const { disputeId } = Route.useParams();
  const disputeIdNum = parseInt(disputeId, 10);
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();

  // Track screen view
  useEffect(() => {
    track('Expense Dispute Chat Screen');
  }, []);

  // Fetch user
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
    enabled: isLoaded && !!isSignedIn,
  });

  // Fetch conversations to get dispute info
  const conversationsQuery = useQuery({
    queryKey: ['conversations'],
    queryFn: () => apiJson<ConversationsResponse>(getToken, '/v1/conversations'),
    enabled: isLoaded && !!isSignedIn,
  });

  // Find the dispute
  const allDisputes = [
    ...(conversationsQuery.data?.unresolved_disputes ?? []),
    ...(conversationsQuery.data?.resolved_disputes ?? []),
  ];
  const dispute = allDisputes.find(
    (d) => d.dispute_type === 'expense' && d.dispute_id === disputeIdNum
  );

  const handleBack = () => {
    navigate({ to: '/app/chat' });
  };

  return (
    <div className='flex h-dvh flex-col overflow-hidden bg-sky-light'>
      <ChatView
        conversation={{
          type: 'expense-dispute',
          disputeId: disputeIdNum,
          itemId: dispute?.item_id ?? 0,
        }}
        currentUser={userQuery.data}
        onBack={handleBack}
        showHeader={true}
        disputeInfo={
          dispute
            ? {
                itemName: dispute.item_name,
                isResolved: dispute.is_resolved,
              }
            : undefined
        }
      />
    </div>
  );
}

export const Route = createFileRoute('/app/chat/expense/$disputeId')({
  component: ExpenseDisputeChatPage,
});
